import React from 'react'
import { EnterpriseSummary } from '../../clients/kyousou_api_client'

interface CompanyInfoSectionProps {
  enterprise: EnterpriseSummary
  title?: string
  className?: string
}

/**
 * 企業の基本情報を表示する共通コンポーネント
 */
const CompanyInfoSection: React.FC<CompanyInfoSectionProps> = ({
  enterprise,
  title = '会社情報',
  className = 'mb-8 border border-main-lightgray rounded p-4',
}) => {
  // レイアウト用のCSS定数
  const itemClasses = 'flex gap-4'
  const labelClasses = 'font-medium text-main-darkgray w-20 flex-shrink-0'
  const valueClasses = 'text-main-black flex-1 min-w-0'

  // 汎用フィールド表示関数
  const renderField = (
    label: string,
    value: string | null | undefined,
    isLink = false
  ) => {
    if (!value) return null
    return (
      <div className={itemClasses}>
        <span className={labelClasses}>{label}</span>
        <p className={valueClasses}>
          {isLink ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-main-green underline text-xs break-all"
            >
              {value}
            </a>
          ) : (
            value
          )}
        </p>
      </div>
    )
  }

  // 業種データの取得（配列・オブジェクト両対応）
  const getIndustryValue = () => {
    if (!enterprise.industryParentCode) return null
    return Array.isArray(enterprise.industryParentCode)
      ? enterprise.industryParentCode[0]?.value
      : enterprise.industryParentCode.value
  }

  // SDGsカテゴリーの取得（型安全に処理）
  const getSdgsCategories = () => {
    if (!('sdgsCategory' in enterprise)) return null
    const sdgsCategory = (enterprise as { sdgsCategory?: { value: string }[] })
      .sdgsCategory
    return sdgsCategory?.map((cat) => cat.value).join('\n') || '未設定'
  }

  // 設立年の表示形式を統一
  const getEstablishYear = () => {
    if (!enterprise.establishDate) return null
    return `${new Date(enterprise.establishDate).getFullYear()}年`
  }

  return (
    <div className={className}>
      <h3 className="text-lg font-bold mb-4">{title}</h3>
      <div className="space-y-4 text-sm">
        {/* 企業名（必須項目のため常に表示） */}
        <div className={itemClasses}>
          <span className={labelClasses}>企業名</span>
          <p className={`${valueClasses} break-all`}>{enterprise.name}</p>
        </div>

        {/* オプション項目（データがある場合のみ表示） */}
        {/* TODO:高田 nameEnを入力してもらうところと保存場所を考える*/}
        {/* {renderField('英語表記', enterprise.nameEn)} */}

        {/* SDGsカテゴリー（特殊な表示形式のため個別処理） */}
        {getSdgsCategories() && (
          <div className={itemClasses}>
            <span className={labelClasses}>
              SDGs
              <br />
              カテゴリー
            </span>
            <p className={`${valueClasses} text-xs leading-relaxed`}>
              {getSdgsCategories()}
            </p>
          </div>
        )}

        {/* その他の企業情報 */}
        {renderField('業種', getIndustryValue())}
        {renderField('所在地', enterprise.address || enterprise.prefecture)}
        {renderField('設立年', getEstablishYear())}
        {/* TODO:高田 各所入力してもらうところと保存場所を考える*/}
        {/* {renderField('企業規模', enterprise.employeeCount)}
        {renderField('平均年齢', enterprise.averageAge)}
        {renderField('資本金', enterprise.capital)}
        {renderField('売上', enterprise.sales)} */}
        {renderField('URL', enterprise.officialUrl, true)}
      </div>
    </div>
  )
}

export default CompanyInfoSection
